/**
 * @fileoverview Student Lessons page ("Studio Quiet") — searchable, filterable
 * list of lesson summaries. Each row deep-links to the read-only lesson view and
 * exposes a quiet rehearse shortcut for assigned lessons — only while the
 * rehearsals kill switch is on, like every other rehearsal control in the
 * portal. With it off, the recordings endpoint 404s and a shortcut would lead
 * to "Rehearsals are turned off".
 */

import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { StudentAuthContext } from "@/App";
import studentApi from "@/lib/studentApi";
import { rehearsalsEnabled } from "@/lib/featureFlags";
import { cn } from "@/lib/utils";
import { Card, Icon, Button, Tag } from "@/components/studio/primitives";
import { SHeader, SEmpty } from "@/components/studio/studentUi";
import ProgramChip from "@/components/ProgramChip";

const PAGE_SIZE = 20;

const formatDuration = (seconds) => {
  if (!seconds) return "";
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s} sec`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function FilterChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full border px-5 py-[9px] font-sans text-[15px] font-semibold capitalize transition-colors duration-150",
        active
          ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
          : "border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
      )}
    >
      {children}
    </button>
  );
}

const StudentLessonsPage = () => {
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const showRehearsals = rehearsalsEnabled(student);
  const [lessons, setLessons] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [instrument, setInstrument] = useState(null);
  const [rehearsedLessons, setRehearsedLessons] = useState(new Set());
  const debounceRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    const ignoreAbort = (err) => {
      if (!controller.signal.aborted) console.error(err);
    };
    studentApi.get("/student/instruments", { signal: controller.signal })
      .then((res) => setInstruments(res.data || []))
      .catch(ignoreAbort);
    // Skip when rehearsals is off — the endpoint 404s and there's nothing to mark.
    if (showRehearsals) {
      studentApi.get("/student/practice/recordings", { signal: controller.signal })
        .then((res) => setRehearsedLessons(new Set((res.data || []).map((r) => r.lesson_id))))
        .catch(ignoreAbort);
    }
    return () => controller.abort();
  }, [showRehearsals]);

  const fetchLessons = useCallback(async (searchVal, instrumentVal, skip = 0) => {
    const params = { limit: PAGE_SIZE, skip };
    if (searchVal) params.search = searchVal;
    if (instrumentVal) params.instrument = instrumentVal;
    const res = await studentApi.get("/student/lessons", { params });
    return res.data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchLessons(activeSearch, instrument);
        if (!cancelled) {
          setLessons(data);
          setHasMore(data.length === PAGE_SIZE);
        }
      } catch (_err) {
        if (!cancelled) setLessons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeSearch, instrument, fetchLessons]);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setActiveSearch(val), 400);
  };

  const clearSearch = () => {
    setSearch("");
    setActiveSearch("");
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchLessons(activeSearch, instrument, lessons.length);
      setLessons((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_SIZE);
    } catch (_err) { /* ignored */ }
    setLoadingMore(false);
  };

  return (
    <div className="mx-auto max-w-[1120px] px-6 pb-32 pt-11 md:px-12" data-testid="student-lessons">
      <SHeader eyebrow="Library" title="Lessons" subtitle="Browse and search all your lesson summaries." />

      {/* Search */}
      <div className="mb-[22px] flex items-center gap-3 rounded-[16px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-[18px]">
        <Icon name="search" size={18} className="text-[var(--text-tertiary)]" />
        <input
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search lessons, concepts, practice items…"
          data-testid="lessons-search"
          className="min-w-0 flex-1 border-none bg-transparent py-[18px] font-sans text-base text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)]"
        />
        {search && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label="Clear search"
            className="text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
          >
            <Icon name="x" size={18} />
          </button>
        )}
      </div>

      {/* Instrument filters */}
      {instruments.length > 0 && (
        <div className="mb-[26px] flex gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterChip active={!instrument} onClick={() => setInstrument(null)}>All</FilterChip>
          {instruments.map((inst) => (
            <FilterChip
              key={inst}
              active={instrument === inst}
              onClick={() => setInstrument(instrument === inst ? null : inst)}
            >
              {inst}
            </FilterChip>
          ))}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="space-y-2.5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-[92px] rounded-[20px]" />
          ))}
        </div>
      ) : lessons.length === 0 ? (
        <Card padding={0}>
          {activeSearch || instrument ? (
            <SEmpty
              icon="search"
              title="No matches"
              body="No lessons match your search. Try a different term or clear the filters."
              cta="Clear filters"
              ctaIcon="refresh"
              onCta={() => { clearSearch(); setInstrument(null); }}
            />
          ) : (
            <SEmpty
              icon="music"
              title="No lessons yet"
              body="Your lesson summaries will appear here after your teacher records a session."
            />
          )}
        </Card>
      ) : (
        <div className="space-y-2.5">
          {lessons.map((lesson) => {
            const rehearsed = rehearsedLessons.has(lesson.lesson_id);
            return (
              <Card
                key={lesson.lesson_id}
                hover
                onPress={() => navigate(`/student/lessons/${lesson.lesson_id}`)}
                data-testid={`lesson-card-${lesson.lesson_id}`}
                className="flex items-center gap-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-sans text-base font-medium text-[var(--text-primary)]">{lesson.title}</div>
                  {lesson.program_title && (
                    <div className="mt-1">
                      <ProgramChip programTitle={lesson.program_title} orgName={lesson.org_name} accent={lesson.org_accent} />
                    </div>
                  )}
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-[13px] text-[var(--text-tertiary)]">
                    <span>{lesson.teacher_name}</span>
                    <span className="text-[var(--text-quaternary)]">·</span>
                    <span>{formatDate(lesson.created_at)}</span>
                    {lesson.duration_seconds > 0 && (
                      <>
                        <span className="text-[var(--text-quaternary)]">·</span>
                        <span className="inline-flex items-center gap-1">
                          <Icon name="clock" size={12} /> {formatDuration(lesson.duration_seconds)}
                        </span>
                      </>
                    )}
                    {lesson.instrument && (
                      <span className="rounded-full bg-[var(--studio-interactive)] px-2 py-0.5 text-[11px] capitalize text-[var(--text-secondary)]">
                        {lesson.instrument}
                      </span>
                    )}
                  </div>
                  {lesson.practice_items?.length > 0 && (
                    <p className="mt-2 truncate font-sans text-[13px] text-[var(--text-secondary)]">
                      Practice: {lesson.practice_items[0]}
                      {lesson.practice_items.length > 1 && ` +${lesson.practice_items.length - 1} more`}
                    </p>
                  )}
                  {lesson.core_concepts?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {lesson.core_concepts.slice(0, 4).map((concept) => (
                        <Tag key={concept}>{concept}</Tag>
                      ))}
                      {lesson.core_concepts.length > 4 && (
                        <span className="self-center font-sans text-[11px] text-[var(--text-quaternary)]">
                          +{lesson.core_concepts.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!showRehearsals ? null : rehearsed ? (
                    <span className="flex h-9 w-9 items-center justify-center text-[var(--state-success)]" title="Rehearsal submitted">
                      <Icon name="check" size={18} />
                    </span>
                  ) : lesson.rehearsal_assigned ? (
                    <button
                      type="button"
                      aria-label="Record rehearsal for this lesson"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/student/rehearsals?lesson=${lesson.lesson_id}`);
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--accent-yellow-text)]"
                    >
                      <Icon name="mic" size={16} strokeWidth={1.8} />
                    </button>
                  ) : null}
                  <Icon name="chevronRight" size={18} className="text-[var(--text-tertiary)]" />
                </div>
              </Card>
            );
          })}

          {hasMore && (
            <div className="pt-4 text-center">
              <Button variant="bare" onPress={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentLessonsPage;
